import InfiniteScroll from 'react-infinite-scroll-component';
import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import classNames from 'classnames';
import { ErrorMessage, Form, FormikProps, FormikProvider } from 'formik';
import { useCallback } from 'react';
import { AddUserModal } from '@/modules/admin-dashboard/components/modals/AddUserModal.tsx';
import { sortedUsersType } from '@/modules/admin-dashboard/components/modals/UpdateDepartmentModal.tsx';
import {
  Avatar,
  Button,
  CustomCheckbox,
  ModalContentWrapper,
  SearchField,
  Spinner,
  TextInputField,
} from 'ui';
import { useIsMobile, useOpen } from 'ui/hooks';
import { AddUserResponse, GetUserListResponse } from 'compliance-api';

interface FormValues {
  name: string;
  userIds: number[];
}

interface AddBusinessUnitModalProps {
  isOpened: boolean;
  departmentId?: number;
  departmentName?: string;
  handleCloseModal: () => void;
  handleSearchChange: (value: string) => void;
  users: InfiniteData<GetUserListResponse> | undefined | sortedUsersType;
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<GetUserListResponse, unknown>>;
  hasNextPage: boolean;
  formik: FormikProps<FormValues>;
  isLoading: boolean;
  isSaving: boolean;
}

export const DepartmentModal = ({
  isOpened,
  handleCloseModal,
  handleSearchChange,
  users,
  fetchNextPage,
  hasNextPage,
  formik,
  isLoading,
  isSaving,
  departmentId = 0,
  departmentName = undefined,
}: AddBusinessUnitModalProps) => {
  const [isCreationOpen, openCreation, closeCreation] = useOpen(false);

  const handleCheck = useCallback(
    (checked: boolean, userId: number) => {
      if (checked) {
        formik.setFieldValue('userIds', [...formik.values.userIds, userId]);
      } else {
        formik.setFieldValue(
          'userIds',
          formik.values.userIds.filter(id => id !== userId),
        );
      }
    },
    [formik],
  );
  const { isMobile } = useIsMobile();

  const handleUserAdded = useCallback(
    (res: AddUserResponse) => {
      handleCheck(true, res.user.id);
    },
    [handleCheck],
  );
  return (
    <ModalContentWrapper
      open={isOpened}
      title={departmentId ? 'Update Department' : 'Add new Department'}
      onClose={handleCloseModal}
    >
      <FormikProvider value={formik}>
        <Form className="flex flex-col space-y-6">
          <div className="flex flex-col space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Department name</label>
              <TextInputField
                disabled={!!departmentName}
                name="name"
                placeholder={departmentName || 'Enter department name...'}
              />
            </div>

            <div>
              <div className="block text-sm font-medium text-gray-700">
                <h3 className="text-[20px] font-semibold">Add Users</h3>
              </div>
              <br />
              <SearchField placeholder="Search" onChange={handleSearchChange} />
              <br />
              <button
                type="button"
                disabled={isSaving || isLoading}
                className={classNames('text-blue-700 mb-3', {
                  'hover:underline ': !isLoading && !isSaving,
                })}
                onClick={openCreation}
              >
                Add user
              </button>
              <InfiniteScroll
                height={isMobile ? 280 : 500}
                next={fetchNextPage}
                dataLength={users?.pages ? users.pages.length : 0}
                hasMore={hasNextPage}
                loader={
                  <div className="flex justify-center items-center h-16 w-full">
                    <Spinner size="md" variant="primary" />
                  </div>
                }
                scrollableTarget="scrollable"
              >
                {users?.pages && users?.pages[0].data.length === 0 && (
                  <div className="flex items-center justify-center text-3xl py-6 mt-[25%]">
                    Users are absent
                  </div>
                )}
                {isLoading && (
                  <div className="flex items-center justify-center mt-[25%]">
                    <Spinner size="lg" variant="primary" />
                  </div>
                )}
                {users?.pages &&
                  users?.pages.map(({ data }) =>
                    data.map(user => (
                      <div key={user.id} className="flex items-center mb-2">
                        <div className="flex gap-4 ml-1">
                          <CustomCheckbox
                            checked={formik.values.userIds.includes(user.id)}
                            onChange={e => handleCheck(e.target.checked, user.id)}
                          />
                          <div className="flex items-center gap-2">
                            <Avatar
                              src={user.avatar_url}
                              firstName={user.first_name}
                              lastName={user.last_name}
                            />
                            <div>
                              {user.first_name} {user.last_name} <br />
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </div>
                    )),
                  )}
              </InfiniteScroll>
              <ErrorMessage name="userIds" component="div" className="text-sm text-red-600" />
            </div>
          </div>

          <div>
            <Button type="button" onClick={formik.submitForm} loading={isSaving}>
              {departmentId ? 'Update Department' : 'Create Department'}
            </Button>
          </div>
        </Form>
      </FormikProvider>
      {isCreationOpen && (
        <AddUserModal open={isCreationOpen} onClose={closeCreation} onSuccess={handleUserAdded} />
      )}
    </ModalContentWrapper>
  );
};
